import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SiteFaqEntry, SiteRelease, SiteShotEntry, listSiteFaq, listSiteReleases, listSiteShots,
} from '../../api/platform';
import { ActionBar, SubTabs } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { useUnsavedGuard } from '../../components/unsaved';
import { ago } from '../admin/SsoShared';
import { invalidateSiteContent } from '../site/useSiteContent';
import { usePlatform } from './PlatformLayout';
import FaqTab from './siteContent/FaqTab';
import ReleasesTab from './siteContent/ReleasesTab';
import ShotsTab from './siteContent/ShotsTab';
import SiteTab from './siteContent/SiteTab';
import { useSiteDraft } from './siteContent/useSiteDraft';

/**
 * What the public site says and shows (design_handoff_site_content, 078):
 * one page, four sub-tabs in `?tab=`, the way Platform services has them.
 *
 *   Site         the Look (accent, headings, corners, favicon) and the Home
 *                and Features copy, with a live preview — a shared DRAFT
 *                that goes live whole with Publish (the ink bar below)
 *   Screenshots  the image library the pages pick from — live on save
 *   FAQ          R12's card, unchanged — live on save
 *   Releases     R12's card, "+ New release" now in its head — live on save
 *
 * Any staff member reads every tab; writing is the Super Admin's
 * ('site.content'), and the server enforces the same line on every route.
 * Without the role every control is disabled and the "+ New" buttons and ⋯
 * menus are gone, while the tabs, the preview and the editors still open.
 *
 * The draft is kept on the server as it is typed, so leaving the page loses
 * nothing: the guard asks only whether this visit's edits should go live
 * first ("Publish and leave"), and tab switches do not ask at all — the
 * publish bar stays on every tab.
 */

type Tab = 'site' | 'screenshots' | 'faq' | 'releases';
const TABS: [Tab, string][] = [['site', 'Site'], ['screenshots', 'Screenshots'], ['faq', 'FAQ'], ['releases', 'Releases']];

export default function SiteContent() {
  const { role } = usePlatform();
  const readOnly = role !== 'superadmin';
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: Tab = TABS.some(([t]) => t === tabParam) ? (tabParam as Tab) : 'site';
  const toast = useToast();

  const [shots, setShots] = useState<SiteShotEntry[] | null>(null);
  const [releases, setReleases] = useState<SiteRelease[] | null>(null);
  const [faq, setFaq] = useState<SiteFaqEntry[] | null>(null);
  const [error, setError] = useState('');
  const [armed, setArmed] = useState(false);

  const draft = useSiteDraft(useCallback((message: string) => toast(message), [toast]));

  const loadShots = () => listSiteShots().then((r) => setShots(r.shots));
  const loadReleases = () => listSiteReleases().then((r) => setReleases(r.releases));
  const loadFaq = () => listSiteFaq().then((r) => setFaq(r.faq));
  useEffect(() => {
    Promise.all([loadShots(), loadReleases(), loadFaq()]).catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A FAQ or release write: reload both lists and say so; the refusal (or null) for the dialog to show. */
  const write = async (fn: () => Promise<unknown>, done: string): Promise<string | null> => {
    try {
      await fn();
      await Promise.all([loadReleases(), loadFaq()]).catch(() => undefined);
      invalidateSiteContent();
      toast(done);
      return null;
    } catch (e) {
      return (e as Error).message || 'That could not be saved.';
    }
  };

  const changes = draft.changes.length;
  const guard = useUnsavedGuard({
    dirty: !readOnly && draft.editedHere && changes > 0,
    what: 'Your unpublished changes to the site',
    body: 'Your changes to the site are kept as a draft, but they are not live yet. Publish them now, or leave them in the draft for later.',
    onSave: () => draft.publish(),
    saveLabel: 'Publish and leave',
    leaveLabel: 'Leave them in the draft',
    busy: draft.publishing,
  });

  const goToTab = (t: Tab) => setParams({ tab: t }, { replace: true });

  return (
    <>
      <div className="pl-page sc-page">
        <div className="fs-page-head pl-head">
          <div className="text">
            <h1>Site content</h1>
            <div className="fs-page-sub sc-sub">
              What the public site says and shows. Page text and the look wait for Publish. Screenshots, FAQ and
              releases go live when you save them.
              {readOnly ? ' Editing needs the Super Admin role.' : ''}
            </div>
          </div>
          <a className="fs-btn neutral" href="/" target="_blank" rel="noreferrer">View live site ↗</a>
        </div>

        <SubTabs
          aria-label="Site content"
          className="sc-tabs"
          items={TABS.map(([t, label]) => ({
            label,
            active: tab === t,
            onClick: () => goToTab(t),
            count: t === 'screenshots' ? shots?.length : t === 'faq' ? faq?.length : t === 'releases' ? releases?.length : undefined,
          }))}
        />

        {error && <div className="ad-err">{error}</div>}

        {/* The draft's conflict and refusals, above whichever tab is open:
            Publish is in the bar on every tab, so its answer must be too. */}
        {draft.conflict && (
          <div className="sc-conflict" role="alert">
            <span className="sc-conflict-text">
              <b>{draft.conflict.by || 'Someone else'} {draft.conflict.theirs ? 'changed the draft' : 'published or discarded the draft'}</b>
              {draft.conflict.at ? ` ${ago(draft.conflict.at)}` : ''} after you last saved it. What is on your screen has not been saved.
            </span>
            <button type="button" className="fs-btn neutral sm" onClick={draft.takeTheirs}>
              {draft.conflict.theirs ? 'Load their draft' : 'Load the live site'}
            </button>
            <button type="button" className="fs-btn primary sm" onClick={draft.keepMine}>Keep mine</button>
          </div>
        )}
        {draft.error && <div className="ad-err" role="alert">{draft.error}</div>}

        {tab === 'site' && (
          draft.loadError ? <div className="ad-err">{draft.loadError}</div>
            : !draft.loaded || !shots ? <section className="fs-card"><div className="st-card-body st-muted">Loading…</div></section>
              : <SiteTab draft={draft} shots={shots} readOnly={readOnly} />
        )}

        {tab === 'screenshots' && (
          shots ? (
            <ShotsTab
              shots={shots}
              published={draft.published}
              readOnly={readOnly}
              onSaved={(saved, message) => {
                setShots((list) => (list?.some((s) => s.id === saved.id)
                  ? list.map((s) => (s.id === saved.id ? saved : s)) : [...(list || []), saved]));
                invalidateSiteContent();
                toast(message);
              }}
            />
          ) : <section className="fs-card"><div className="st-card-body st-muted">Loading…</div></section>
        )}

        {tab === 'faq' && <FaqTab faq={faq} readOnly={readOnly} write={write} />}
        {tab === 'releases' && <ReleasesTab releases={releases} readOnly={readOnly} write={write} />}
      </div>

      {/* The draft's bar: on every tab, while anything differs from what is
          live. Outside .pl-page on purpose: a sticky grid item cannot leave
          its own row, and this one floats over the page as it scrolls. */}
      {!readOnly && changes > 0 && (
        <ActionBar className="ink sc-publish">
          <b>{changes} unpublished {changes === 1 ? 'change' : 'changes'}</b>
          <span className="sc-publish-note">Pages and look go live together.</span>
          <button
            type="button" className="fs-btn text sm sc-publish-discard" disabled={draft.publishing}
            onBlur={() => setArmed(false)}
            onClick={() => {
              // The codebase's two-click confirm: a draft two people may have
              // typed into is not thrown away by one stray click.
              if (!armed) { setArmed(true); return; }
              setArmed(false);
              void draft.discard();
            }}
          >
            {armed ? `Discard ${changes === 1 ? 'it' : `all ${changes}`}?` : 'Discard'}
          </button>
          <button
            type="button" className="fs-btn primary sm" disabled={draft.publishing}
            onClick={() => { draft.publish().catch(() => undefined); }}
          >
            {draft.publishing ? 'Publishing…' : 'Publish'}
          </button>
        </ActionBar>
      )}

      {guard.node}
    </>
  );
}
